<template>
  <b-card no-body class="border-0 shadow">
    <b-card-header
      class="d-flex justify-content-between align-items-center cursor-pointer"
      @click="expanded = !expanded"
    >
      <h5 class="mb-0">{{ title }}</h5>
      <feather-icon
        :icon="expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'"
        size="18"
        class="text-muted"
      />
    </b-card-header>

    <b-collapse v-model="expanded">
      <b-card-body class="p-0 overflow-auto">
        <div class="card-table">
          <b-table
            small
            bordered
            class="okr-table"
            :items="tasks"
            :fields="fields"
          >
            <template #cell(description)="data">
              <div class="d-flex align-items-center">
                <span
                  class="circle-icon mr-2"
                  :class="`bg-${getIconColor(data.item.icon)}`"
                >
                  {{ data.item.icon }}
                </span>
                <div class="d-flex flex-column">
                  <h5 class="mb-0">{{ data.value }}</h5>
                </div>
              </div>
            </template>

            <template #cell(kr)="data">
              <span v-if="data.item.isCurrency">{{ data.value | toCurrency }}</span>
              <span v-else>{{ data.value }}</span>
            </template>

            <template #cell(actual)="data">
              <span v-if="data.item.isCurrency">{{ data.value | toCurrency }}</span>
              <span v-else>{{ data.value }}</span>
            </template>

            <template #cell(diff)="data">
              <span :class="diffClass(data.item)">
                {{ diffLabel(data.item) }}
              </span>
            </template>

            <template #cell(progress)="data">
              <div class="d-flex align-items-center">
                <b-progress
                  :value="data.value"
                  :max="100"
                  variant="success"
                  height="1rem"
                  class="progress-bar-custom flex-grow-1 mr-1"
                />
                <span class="progress-percent">{{ data.item.percent }}%</span>
              </div>
            </template>
          </b-table>
        </div>
      </b-card-body>
    </b-collapse>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardBody,
  BCardHeader,
  BTable,
  BCollapse,
  BProgress,
} from "bootstrap-vue";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";

export default {
  name: "TaskTracker",
  components: {
    BCard,
    BCardBody,
    BCardHeader,
    BTable,
    BCollapse,
    BProgress,
    FeatherIcon,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    tasks: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      expanded: false,
    };
  },
  computed: {
    fields() {
      return [
        { key: "description", label: "ETAPA" },
        { key: "kr", label: "PLANEJADO", thClass: ["text-left", "th-planned"] },
        { key: "actual", label: "REALIZADO", thClass: ["text-left", "th-realized"] },
        { key: "diff", label: "DIFERENÇA", thClass: ["text-left"] },
        { key: "progress", label: "PROGRESSO" },
      ];
    },
  },
  methods: {
    diffValue(item) {
      return (item.actual || 0) - (item.kr || 0);
    },
    diffLabel(item) {
      const diff = this.diffValue(item);
      const sign = diff > 0 ? "+" : "";
      if (item.isCurrency) {
        const formatted = this.$options.filters?.toCurrency?.(Math.abs(diff)) ?? Math.abs(diff);
        return `${diff < 0 ? "-" : sign}${formatted}`;
      }
      return `${sign}${diff}`;
    },
    diffClass(item) {
      const diff = this.diffValue(item);
      if (diff < 0) return "text-danger font-weight-bolder";
      if (diff > 0) return "text-success font-weight-bolder";
      return "text-muted font-weight-bolder";
    },
    getIconColor(icon) {
      const colors = {
        EA: "danger",
        EE: "info",
        EP: "success",
        FEP: "primary",
        MXA: "warning",
        PA: "danger",
        AS: "info",
        SL: "secondary",
        OKR: "dark",
      };
      return colors[icon] || "dark";
    },
  },
};
</script>

<style lang="scss" scoped>
.okr-table {
  background: transparent;
  border: none !important;

  ::v-deep table {
    background: transparent;
  }

  ::v-deep thead tr {
    background-color: #343d55;
    border-radius: 20px;
  }
  ::v-deep th {
    border-color: transparent !important;
    font-size: 1rem;
    padding: 0.5rem 0.75rem;
    white-space: nowrap;
  }

  /* mesmas cores dos funis */
  ::v-deep th.th-planned {
    color: #6ea8ff !important;
  }
  ::v-deep th.th-realized {
    color: #48da89 !important;
  }

  ::v-deep td {
    border-color: transparent !important;
    font-size: 1rem;
    padding: 14px 0.75rem;
    vertical-align: middle;
  }
}

.circle-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
  color: white;
  flex-shrink: 0;
}

.card-table {
  min-width: 700px;
}

.progress-bar-custom {
  border-radius: 8px;
  overflow: hidden;
  min-width: 80px;
}

.progress-bar-custom .progress-bar {
  border-radius: 8px;
  transition: width 0.3s ease;
}

.progress-percent {
  min-width: 42px;
  text-align: right;
  font-weight: 600;
  font-size: 0.85rem;
}

.cursor-pointer {
  cursor: pointer;
}

/* Ajustes para dispositivos móveis */
@media (max-width: 991px) {
  .okr-table ::v-deep th {
    font-size: 0.875rem;
  }

  .okr-table ::v-deep td {
    padding: 10px 0.5rem;
  }
}
</style>
